/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  GetTradesOptions,
  GetTradesQuery,
  GetTradesStatus200,
  GetTradesStatus400,
  GetTradesStatus404,
  GetTradesStatus500,
} from '../types/GetTrades'
import type {
  InfiniteData,
  QueryKey,
  QueryClient,
  InfiniteQueryObserverOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'
import { getTrades } from '../clients/getTrades'
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'

export const getTradesInfiniteQueryKey = ({ query }: Omit<GetTradesOptions, 'headers'> = {}) =>
  [{ url: '/v1/trades', infinite: true }, ...(query ? [query] : [])] as const

type GetTradesInfiniteQueryKey = ReturnType<typeof getTradesInfiniteQueryKey>

export function getTradesInfiniteQueryOptions(
  { query }: GetTradesOptions = {},
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const queryKey = getTradesInfiniteQueryKey({ query })
  return infiniteQueryOptions<
    GetTradesStatus200,
    ResponseErrorConfig<GetTradesStatus400 | GetTradesStatus404 | GetTradesStatus500>,
    InfiniteData<GetTradesStatus200>,
    typeof queryKey,
    NonNullable<GetTradesQuery['page']>
  >({
    queryKey,
    queryFn: async ({ signal, pageParam }) => {
      query = {
        ...(query ?? {}),
        ['page']: pageParam as unknown as GetTradesQuery['page'],
      } as GetTradesQuery
      return getTrades({
        ...config,
        query,
        signal: config.signal ?? signal,
        throwOnError: true,
      }).unwrap()
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      Array.isArray(lastPage) && lastPage.length === 0 ? undefined : lastPageParam + 1,
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) =>
      firstPageParam <= 1 ? undefined : firstPageParam - 1,
  })
}

/**
 * @description Role-based date window: public callers are limited to the last month; authenticated callers may query up to the last six months. If `start` is omitted it defaults to the role-based minimum. If credentials are supplied but invalid, the request is rejected as unauthorized.
 * @summary Get a filtered, paginated list of trades
 * {@link /v1/trades}
 */
export function useGetTradesInfinite<
  TQueryFnData = GetTradesStatus200,
  TError = ResponseErrorConfig<GetTradesStatus400 | GetTradesStatus404 | GetTradesStatus500>,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = GetTradesInfiniteQueryKey,
  TPageParam = NonNullable<GetTradesQuery['page']>,
>(
  { query }: { query?: GetTradesOptions['query'] | (() => GetTradesOptions['query']) } = {},
  options: {
    query?: Partial<
      InfiniteQueryObserverOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const resolvedParams = { query: typeof query === 'function' ? query() : query }
  const queryKey = resolvedOptions?.queryKey ?? getTradesInfiniteQueryKey(resolvedParams)

  const queryResult = useInfiniteQuery(
    {
      ...getTradesInfiniteQueryOptions(resolvedParams, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as InfiniteQueryObserverOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >,
    queryClient,
  ) as UseInfiniteQueryResult<TData, TError> & { queryKey: TQueryKey }

  queryResult.queryKey = queryKey as TQueryKey

  return queryResult
}
